import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { formatDistance } from 'date-fns';
import { FaHeart, FaRegHeart, FaComment } from 'react-icons/fa';
import { likePost, unlikePost, deletePost, isPostLikedByUser } from '../../services/postService';
import { getComments } from '../../services/commentService';
import { useAuth } from '../../contexts/AuthContext';
import Avatar from '../common/Avatar';
import CommentList from './CommentList';
import PostOptions from './PostOptions';
import ProgressiveImage from '../common/ProgressiveImage';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import smartImagePreloader from '../../utils/smartImagePreloader';

import './PostCard.css';

/**
 * PostCard component displays a single post in the feed
 * @param {Object} props - Component props
 * @param {Object} props.post - Post data object
 * @param {Function} props.onPostUpdate - Callback when post is updated (like/unlike)
 * @param {Function} props.onPostDelete - Callback when post is deleted
 */
const PostCard = ({ post, onPostUpdate, onPostDelete }) => {
  const { user } = useAuth();
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  
  // Intersection observer for lazy loading
  const [ref, isIntersecting, hasIntersected] = useIntersectionObserver({
    rootMargin: '200px 0px' // Start loading 200px before visible
  });
  
  // Trigger preloading when post becomes visible
  useEffect(() => {
    if (isIntersecting && !hasIntersected) {
      // Get image URL for this post
      let imageUrl = null;
      if (post.post_images && post.post_images.length > 0) {
        imageUrl = post.post_images[0].url;
      } else if (post.image_url) {
        imageUrl = post.image_url;
      }
      
      if (imageUrl && !smartImagePreloader.isPreloaded(imageUrl)) {
        console.log(`🎯 Preloading image for post ${post.id}: ${imageUrl}`);
        smartImagePreloader.preloadSingleImage(imageUrl);
      }
    }
  }, [isIntersecting, hasIntersected, post.id, post.post_images, post.image_url]);
  
  // Function to correct URL case sensitivity
  const correctImageUrl = (url) => {
    if (!url) return null;
    
    // Fix the case sensitivity issue in the Supabase URL
    let correctedUrl = url;
    
    // Handle different case variations
    const incorrectPatterns = [
      /sttvwhschLpqwuLLowvb\.supabase\.co/g,
      /sttvwhschLpqwullowvb\.supabase\.co/g,
      /sttvwhschlpgwullowvb\.supabase\.co/g
    ];
    
    incorrectPatterns.forEach(pattern => {
      correctedUrl = correctedUrl.replace(pattern, 'sttvwhschlpgwullowvb.supabase.co');
    });
    
    return correctedUrl;
  };
  
  // Check if the current user has liked this post
  const [isLiked, setIsLiked] = useState(false);
  
  // Check if user has liked this post
  useEffect(() => {
    const checkIfLiked = async () => {
      if (!user || !post.id) return;
      try {
        const liked = await isPostLikedByUser(post.id);
        setIsLiked(liked);
      } catch (error) {
        console.error('Error checking if post is liked:', error);
      }
    };
    
    checkIfLiked();
  }, [user, post.id]);
  
  // Debug logging
  console.log(`[PostCard ${post.id}] Post data:`, {
    title: post.title,
    likes_count: post.likes_count,
    comments_count: post.comments_count
  });
  
  // Fetch comments when component mounts or post.id changes
  useEffect(() => {
    const fetchPostComments = async () => {
      if (!post.id) return;
      setLoadingComments(true);
      try {
        const fetchedComments = await getComments(post.id);
        setComments(fetchedComments || []);
        console.log(`[PostCard ${post.id}] Fetched comments:`, fetchedComments);
      } catch (error) {
        console.error(`[PostCard ${post.id}] Error fetching comments:`, error);
      } finally {
        setLoadingComments(false);
      }
    };

    fetchPostComments();
  }, [post.id]);
  
  // Format the post date
  const formattedDate = post.created_at 
    ? formatDistance(new Date(post.created_at), new Date(), { addSuffix: true })
    : 'recently';
  
  // Handle like/unlike post
  const handleLikeToggle = async () => {
    if (isLiking || !user) return;
    
    try {
      setIsLiking(true);
      
      // Optimistic UI update
      setIsLiked(!isLiked);
      
      const updatedPost = {
        ...post,
        likes_count: isLiked ? Math.max(0, (post.likes_count || 0) - 1) : (post.likes_count || 0) + 1
      };
      
      // Update the post in the parent component
      onPostUpdate(updatedPost);
      
      // Make API call
      if (isLiked) {
        await unlikePost(post.id);
      } else {
        await likePost(post.id);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      
      // Revert optimistic update on failure
      setIsLiked(isLiked); // Revert to original state
      onPostUpdate(post);
    } finally {
      setIsLiking(false);
    }
  };
  
  // Handle post deletion
  const handleDeleteClick = async () => {
    setConfirmDelete(true);
  };
  
  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);
      await deletePost(post.id);
      onPostDelete(post.id);
    } catch (error) {
      console.error('Error deleting post:', error);
      setIsDeleting(false);
      setConfirmDelete(false);
    }
  };
  
  // Handle comment updates
  const handleCommentAdded = (newComment) => {
    setComments(prevComments => [...prevComments, newComment]);
    const updatedPost = {
      ...post,
      comments_count: (post.comments_count || 0) + 1
    };
    onPostUpdate(updatedPost);
  };
  
  const handleCommentDeleted = (commentId) => {
    setComments(prevComments => prevComments.filter(comment => comment.id !== commentId));
    const updatedPost = {
      ...post,
      comments_count: Math.max(0, (post.comments_count || 0) - 1)
    };
    onPostUpdate(updatedPost);
  };
  
  return (
    <div className={`feed-post-card ${isDeleting ? 'post-loading' : ''}`} ref={ref}>
      <div className="post-header">
        <Link to={`/profile/${post.user_id}`} className="post-user-info">
          <Avatar 
            src={post.user?.avatar_url} 
            alt={post.user?.username || 'User'} 
            username={post.user?.username}
            className="post-avatar"
          />
          <div>
            <h3 className="post-username">{post.user?.username || 'Unknown User'}</h3>
            <p className="post-timestamp">{formattedDate}</p>
          </div>
        </Link>
        
        {user?.id === post.user_id && (
          <PostOptions 
            onDelete={handleDeleteClick} 
            disabled={isDeleting}
          />
        )}
      </div>
      
      <div className="post-content">
        {post.content}
      </div>
      
      {post.post_images && post.post_images.length > 0 ? (
        <div className="post-image">
          <ProgressiveImage
            src={post.post_images[0].url}
            alt="Post"
            className="post-progressive-image"
            context="feed-post"
            onError={() => {
              console.error("Image failed to load:", post.post_images[0].url);
            }}
            onLoad={() => {
              console.log("Image loaded successfully:", post.post_images[0].url);
            }}
          />
        </div>
      ) : post.image_url ? (
        <div className="post-image">
          <ProgressiveImage
            src={post.image_url}
            alt="Post"
            className="post-progressive-image"
            context="feed-post"
            onError={() => {
              console.error("Fallback image failed to load:", post.image_url);
            }}
          />
        </div>
      ) : null}
      
      <div className="post-actions">
        <button 
          className={`post-action-button ${isLiked ? 'liked' : ''}`}
          onClick={handleLikeToggle}
          disabled={isLiking || !user}
          aria-label={isLiked ? "Unlike" : "Like"}
        >
          <span className="icon">
            {isLiked ? <FaHeart /> : <FaRegHeart />}
          </span>
          <span className="post-action-count">
            {post.likes_count || 0}
          </span>
        </button>
        
        <button 
          className="post-action-button"
          onClick={() => setShowComments(!showComments)}
          aria-label={showComments ? "Hide comments" : "Show comments"}
        >
          <span className="icon">
            <FaComment />
          </span>
          <span className="post-action-count">
            {post.comments_count || comments.length || 0}
          </span>
        </button>
      </div>
      
      {confirmDelete && (
        <div className="delete-confirmation">
          <button 
            className="confirm-delete-button"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
          <button 
            className="cancel-delete-button"
            onClick={() => setConfirmDelete(false)}
            disabled={isDeleting}
          >
            Cancel
          </button>
        </div>
      )}
      
      {showComments && (
        <div className="comments-section">
          {loadingComments ? (
            <p>Loading comments...</p> 
          ) : (
            <CommentList 
              comments={comments} 
              postId={post.id}
              currentUser={user}
              onCommentAdded={handleCommentAdded}
              onCommentDeleted={handleCommentDeleted}
              expanded={true}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default PostCard; 